import {
  addDoc,
  collection,
  doc,
  getDocs,
  getFirestore,
  orderBy,
  query,
  where,
} from "firebase/firestore/lite";
import { EMessageRole, TMessage, TSession } from "../models/ai.model";
import { apiWorker, TResponse } from "./apiWorker";

export async function getSessions({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: TResponse<TSession>) => void;
  onError?: (data: any) => void;
}) {
  const db = getFirestore();
  const userId = localStorage.getItem("uid") || "";

  const queried = query(
    collection(db, "sessions"),
    where("userId", "==", userId)
  );

  const { docs } = await getDocs(queried);

  if (docs.length === 0) {
    onError?.("No session found");
    return;
  }

  const sessions = docs.map((doc) => {
    return { id: doc.id, ...doc.data() } as TSession;
  });

  const sortedSessions = sessions.sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  onSuccess?.({
    content: sortedSessions,
    total: sortedSessions.length,
    limit: 100,
    offset: 0,
  });
}

export async function getActiveSession({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: TSession) => void;
  onError?: (data: any) => void;
}) {
  const db = getFirestore();
  const userId = localStorage.getItem("uid") || "";

  const queried = query(
    collection(db, "sessions"),
    where("userId", "==", userId),
    where("status", "==", "active")
  );

  const { docs } = await getDocs(queried);

  if (docs.length === 0) {
    // create a new session
    const sessionRef = collection(db, "sessions");

    const sessionData = {
      userId,
      status: "active",
      name: "New Session",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const sessionDoc = await addDoc(sessionRef, sessionData);
    const newSession = {
      ...sessionData,
      id: sessionDoc.id,
    } as TSession;

    onSuccess?.(newSession);
    return;
  }
  const session = docs[0].data();

  onSuccess?.({ ...session, id: docs[0].id } as TSession);
}

export async function getMessages({
  sessionId,
  onSuccess,
  onError,
}: {
  sessionId: string;
  onSuccess?: (data: TResponse<TMessage>) => void;
  onError?: (data: any) => void;
}) {
  if (!sessionId) return onError?.("Session ID is required");

  const db = getFirestore();

  const docRef = doc(db, "sessions", sessionId);
  const messagesRef = collection(docRef, "messages");
  const queried = query(messagesRef, orderBy("createdAt", "asc"));

  try {
    const { docs } = await getDocs(queried);

    if (docs.length === 0) {
      onError?.("No messages found");
      return;
    }

    const messages = docs.map((doc) => ({
      ...doc.data(),
      id: doc.id,
    })) as TMessage[];

    onSuccess?.({
      content: messages,
      total: messages.length,
      limit: 100,
      offset: 0,
    });
  } catch (error) {
    onError?.(error);
  }
}

export async function createMessage({
  sessionId,
  content,
  onSuccess,
  onError,
}: {
  sessionId: string;
  content: any;
  onSuccess?: (data: TResponse<TMessage>) => void;
  onError?: (data: any) => void;
}) {
  const db = getFirestore();
  const docRef = doc(db, "sessions", sessionId);
  const sessionRef = collection(docRef, "messages");

  const message = {
    role: EMessageRole.USER,
    sessionId,
    content,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  const messageDoc = await addDoc(sessionRef, message);

  const newMessage = {
    ...message,
    id: messageDoc.id,
  } as TMessage;

  apiWorker({
    url: "/ai/message",
    method: "POST",
    body: {
      message: content,
    },
    onSuccess: (data) => {
      onSuccess?.({
        content: [newMessage, ...data.content],
        total: 1,
        limit: 1,
        offset: 0,
      });
    },
    onError,
  });
}

export async function improveWriting({
  text,
  onSuccess,
  onError,
}: {
  text: string;
  onSuccess?: (data: { text: string; textOutput: string }) => void;
  onError?: (data: any) => void;
}) {
  apiWorker({
    url: "/ai/improve",
    method: "POST",
    body: {
      message: text,
    },
    onSuccess,
    onError,
  });
}
